import {
  AsyncPipe,
  DatePipe,
  NgClass,
  NgTemplateOutlet,
  SlicePipe,
  TitleCasePipe,
} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';

import { Router } from '@angular/router';
import {
  AppointmentService,
  ClassService,
  TimeconversionService,
} from '@fitness-force/api';
import { CONSTANTS_DATA } from '@fitness-force/constants';
import {
  AppointmentStatus,
  ClassStatus,
  ScheduleType,
  CalendarView,
} from '@fitness-force/enum';
import type {
  Appointment,
  Class,
  ListAppointmentRequest,
  ListClassRequest,
  Resource,
  selectedCalendarView,
  selectedResourceView,
  Staff,
  Room,
  TENANTSINTERFACE,
} from '@fitness-force/models';

import { Store, createFeatureSelector, createSelector } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { StyleClassModule } from 'primeng/styleclass';
import { BehaviorSubject, Observable, catchError, forkJoin, map, of } from 'rxjs';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

// Import Syncfusion modules
import {
  ScheduleModule,
  DayService,
  WeekService,
  WorkWeekService,
  MonthService,
  AgendaService,
  TimelineViewsService,
  TimelineMonthService,
  ResizeService,
  DragAndDropService,
  PrintService,
  ScheduleComponent,
  EventSettingsModel,
  View,
  GroupModel,
  ActionEventArgs,
  CellClickEventArgs,
  EventClickArgs,
  TimeScaleModel,
  EventRenderedArgs,
} from '@syncfusion/ej2-angular-schedule';
import { ROOM_LIST_STATE, STAFF_STATE } from '@fitness-force/state';

@Component({
  selector: 'calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  providers: [
    DayService,
    WeekService,
    WorkWeekService,
    MonthService,
    AgendaService,
    TimelineViewsService,
    TimelineMonthService,
    ResizeService,
    DragAndDropService,
    PrintService,
    DatePipe,
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    SelectModule,
    ButtonModule,
    AvatarModule,
    StyleClassModule,
    ShortNamePipe,
    TranslateModule,
    ProgressSpinnerModule,
    ScheduleModule,
    AsyncPipe,
    DatePipe,
    NgClass,
    NgTemplateOutlet,
    SlicePipe,
    TitleCasePipe,
  ],
})
export class CalendarComponent implements OnInit, AfterViewInit {
  private appointmentService = inject(AppointmentService);
  private classService = inject(ClassService);
  private store = inject<Store<any>>(Store);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private timeService = inject(TimeconversionService);
  private fb = inject(UntypedFormBuilder);
  datepipe = inject(DatePipe);
  cdr = inject(ChangeDetectorRef);

  readonly emitEvents = output<any>();
  readonly disableAppt = input<boolean>(false);
  readonly disableclass = input<boolean>(false);
  readonly showOnlyClasses = input<boolean>(false);
  readonly showOnlyAppointments = input<boolean>(false);
  readonly onlyTrialAllowed = input<boolean>(false);

  public readonly scheduleObj = viewChild<ScheduleComponent>('scheduleObj');

  // Observables and data storage
  getAccessTenantList$: Observable<any>;
  getAccessedStaffList$ = new BehaviorSubject<Staff[]>([]);
  appointmentList$: Appointment[] = [];
  classList$: Class[] = [];
  roomList$ = new BehaviorSubject<Room[]>([]);

  // Component state
  selectedDateTime: any;
  currentDate: Date = new Date();
  currentView: View = 'Week'; // Default view
  trainerResources: any[] = [];
  studioResource: any[] = [];
  isTrainerView: boolean = false;
  isStudioView: boolean = false;
  calendarStartDate: any;
  calendarEndDate: any;
  CalendarFullScreen = false;

  // Event objects
  eventObject: any | undefined;
  appointmentObject: Appointment | undefined;
  classObject: Class | undefined;

  // Form control
  fitnessCalendarForm!: UntypedFormGroup;

  // Calendar configuration
  calendarResourceView = CONSTANTS_DATA.CalendarResourceView;
  calendarView = CONSTANTS_DATA.CalendarView;
  selectedCalendarView: selectedCalendarView;
  selectedCalendarResourceView: selectedResourceView;
  resource: Resource;
  loading = new BehaviorSubject<boolean>(false);
  tenantId = 0;

  // Syncfusion specific properties
  public eventSettings: EventSettingsModel = {
    dataSource: [],
    fields: {
      id: 'Guid',
      subject: { name: 'Subject' },
      startTime: { name: 'StartTime' },
      endTime: { name: 'EndTime' },
      isAllDay: { name: 'IsAllDay' },
    },
    enableTooltip: false,
  };

  public group: GroupModel = {
    resources: [],
    allowGroupEdit: false,
    byDate: false,
  };

  public timeScaleOption: TimeScaleModel = {
    enable: true,
    interval: 60,
    slotCount: 2,
  };

  constructor() {
    // Initialize the form with default values
    this.fitnessCalendarForm = this.fb.group({
      calendarViewMob: [''],
      resourceViewMob: [''],
    });
  }

  ngOnInit(): void {
    this.componentInit();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const scheduleObj = this.scheduleObj();
      if (scheduleObj) {
        scheduleObj.currentView = this.currentView;
        this.loadDataForCurrentView(true);
      }
    }, 100);
  }

  @HostListener('window:visibilitychange', ['$event'])
  onVisibilityChange(event: Event): void {
    const visibilityState = (event.target as Document)?.visibilityState;
    if (
      visibilityState?.toLowerCase() === 'visible' &&
      this.calendarStartDate &&
      this.calendarEndDate
    ) {
      this.loadDataForCurrentView(true);
    }
  }

  componentInit() {
    // Get initial view preferences from localStorage
    const localStorageCalVal =
      (localStorage.getItem('SELECTED_CAL_VIEW') as View) || 'Month';
    const localStorageCalResVal =
      localStorage.getItem('SELECTED_RES_VIEW') || '';

    this.currentView = localStorageCalVal;
    this.isTrainerView = localStorageCalResVal === 'trainer';
    this.isStudioView = localStorageCalResVal === 'studio';

    this.selectedCalendarView =
      this.calendarView.find((c) => c.value === localStorageCalVal) ??
      this.calendarView[0];
    this.selectedCalendarResourceView =
      this.calendarResourceView.find(
        (c) => c.value === localStorageCalResVal,
      ) ?? this.calendarResourceView[0];

    // Initialize Group settings based on initial view
    this.updateGroupSettings();

    // Get tenant ID from store
    this.store
      .select((state) => state.tenants?.selected_tenant?.id)
      .subscribe((tenantId) => {
        if (tenantId) {
          this.tenantId = tenantId;
        }
      });

    // Fetch data from store
    this.GetStaffs();
    this.GetRooms();
  }

  // Syncfusion Event Handlers
  onScheduleCreated(args: any): void {
    // Initial setup after schedule is created
  }

  onEventRendered(args: EventRenderedArgs): void {
    if (args.data?.OriginalData) {
      const primeFlexClasses = this.getPrimeFlexClasses(
        args.data.CategoryColor.toLowerCase(),
      );
      args.element.classList.add(...primeFlexClasses);
      args.element.style.borderColor = args.data.CategoryColor;
      args.element.style.height = 'auto';
      if (this.currentView === 'Agenda') {
        args.element.style.marginBottom = '10px';
      }
      const appointment = args.data.OriginalData;
      const element = args.element;
      const status = appointment.status;
      const isCancelled = status === AppointmentStatus.Cancelled;
      const isVirtual = appointment.is_virtual;

      if (isCancelled) {
        element.classList.add('line-through');
      }

      if (isVirtual) {
        element.classList.add('virtual-class');
      }
    }
  }

  getPrimeFlexClasses(color: string): string[] {
    switch (color) {
      case 'blue':
        return ['bg-blue-800', 'text-white'];
      case 'orange':
        return ['bg-orange-800', 'text-white'];
      case 'green':
        return ['bg-green-800', 'text-white'];
      case 'purple':
        return ['bg-purple-800', 'text-white'];
      case 'pink':
        return ['bg-pink-800', 'text-white'];
      case 'cyan':
        return ['bg-cyan-800', 'text-white'];
      case 'yellow':
        return ['bg-yellow-800', 'text-white'];
      case 'indigo':
        return ['bg-indigo-800', 'text-white'];
      case 'teal':
        return ['bg-teal-800', 'text-white'];
      case 'bluegray':
        return ['bg-bluegray-800', 'text-white'];
      case 'gray':
        return ['bg-gray-800', 'text-white'];
      default:
        return ['bg-gray-800', 'text-white'];
    }
  }

  onActionBegin(args: ActionEventArgs): void {
    // Handle toolbar interactions if needed
  }

  onActionComplete(args: ActionEventArgs): void {
    // Load data when view or date range changes
    if (
      args.requestType === 'viewNavigate' ||
      args.requestType === 'dateNavigate' ||
      args.requestType === 'toolBarItemRendered'
    ) {
      this.currentDate = this.scheduleObj()?.selectedDate ?? new Date();
      this.currentView = this.scheduleObj()?.currentView ?? 'Week';
      const displayView =
        this.scheduleObj()?.activeViewOptions.displayName ?? '';

      // If switching to TimelineDay view, make sure resources are updated
      if (this.currentView === 'TimelineDay') {
        this.onResourceViewChange(displayView);
      } else {
        this.isTrainerView = false;
        this.isStudioView = false;
        localStorage.setItem('SELECTED_CAL_VIEW', this.currentView);
        this.updateGroupSettings();
      }
      this.loadDataForCurrentView(true);
    }
  }

  onResourceViewChange(displayName: string): void {
    if (displayName === 'Trainer View') {
      this.isTrainerView = true;
      this.isStudioView = false;
      localStorage.setItem('SELECTED_RES_VIEW', 'trainer');
      this.updateTrainerResources();
    } else if (displayName === 'Studio View') {
      this.isTrainerView = false;
      this.isStudioView = true;
      localStorage.setItem('SELECTED_RES_VIEW', 'studio');
      this.updateStudioResources();
    }
    this.updateGroupSettings();
  }

  loadDataForCurrentView(reloadData: boolean = true): void {
    const view = this.scheduleObj()?.getCurrentViewDates();

    if (view && view.length > 0) {
      this.calendarStartDate = this.timeService.getStartOfDayUTC(view[0]);

      let endDate = view[view.length - 1];
      if (this.currentView === 'Agenda' || this.currentView === 'Day') {
        endDate = new Date(view[0]);
      } else if (this.currentView === 'TimelineYear') {
        endDate = new Date(view[0].getFullYear(), 11, 31);
      } else if (
        this.currentView === 'Week' ||
        this.currentView === 'WorkWeek'
      ) {
        endDate = new Date(view[view.length - 1]);
      } else {
        endDate = new Date(view[view.length - 1]);
      }
      this.calendarEndDate = this.timeService.getEndOfDayUTC(endDate);
    } else {
      const currentDate = this.currentDate || new Date();

      if (this.currentView === 'Day' || this.currentView === 'Agenda') {
        this.calendarStartDate = this.timeService.getStartOfDayUTC(currentDate);

        const nextDay = new Date(currentDate);
        nextDay.setDate(nextDay.getDate() + 1);
        this.calendarEndDate = this.timeService.getEndOfDayUTC(nextDay);
      } else if (
        this.currentView === 'Week' ||
        this.currentView === 'WorkWeek'
      ) {
        const firstDayOfWeek = new Date(currentDate);
        const day = currentDate.getDay();
        const diff = currentDate.getDate() - day + (day === 0 ? -6 : 1);
        firstDayOfWeek.setDate(diff);

        const lastDayOfWeek = new Date(firstDayOfWeek);
        lastDayOfWeek.setDate(lastDayOfWeek.getDate() + 6);

        this.calendarStartDate =
          this.timeService.getStartOfDayUTC(firstDayOfWeek);
        this.calendarEndDate = this.timeService.getEndOfDayUTC(lastDayOfWeek);
      } else if (this.currentView === 'Month') {
        const firstDayOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          1,
        );
        const lastDayOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0,
        );

        const firstDay = new Date(firstDayOfMonth);
        const dayOfWeek = firstDay.getDay();
        firstDay.setDate(
          firstDay.getDate() - (dayOfWeek === 0 ? 7 : dayOfWeek) + 1,
        );

        const lastDay = new Date(lastDayOfMonth);
        const lastDayOfWeekDay = lastDay.getDay();
        lastDay.setDate(
          lastDay.getDate() + (lastDayOfWeekDay === 0 ? 0 : 7 - lastDayOfWeekDay),
        );

        this.calendarStartDate = this.timeService.getStartOfDayUTC(firstDay);
        this.calendarEndDate = this.timeService.getEndOfDayUTC(lastDay);
      } else {
        const firstDayOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          1,
        );
        const lastDayOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0,
        );

        this.calendarStartDate =
          this.timeService.getStartOfDayUTC(firstDayOfMonth);
        this.calendarEndDate = this.timeService.getEndOfDayUTC(lastDayOfMonth);
      }
    }

    if (this.calendarStartDate && this.calendarEndDate && reloadData) {
      this.GetClassAppointments(this.calendarStartDate, this.calendarEndDate);
    }
  }

  onEventClick(args: EventClickArgs): void {
    args.cancel = true; // Prevent default behavior
    const eventData = args.event; // This is the clicked event data
    this.openClassAppointmentPopup(eventData);
  }

  onCellClick(args: CellClickEventArgs): void {
    args.cancel = true; // Prevent default editor
  }

  GetStaffs() {
    this.store.dispatch({ type: 'FETCH_STAFF_LIST' });
    this.store.select(STAFF_STATE).subscribe((data: any) => {
      if (data && data.staffList && data.staffList.length > 0) {
        this.getAccessedStaffList$.next(
          data.staffList.filter((o: any) => {
            return (
              String(o.status).toLowerCase() == 'active' && o.can_take_class
            );
          }),
        );
        this.trainerResources = [];
        this.getAccessedStaffList$.value.forEach((o: any) => {
          this.resource = {
            id: o.id,
            name: o.name,
            img: o.ff_picture,
            color: '',
          };
          this.trainerResources.push(this.resource);
        });
        this.updateTrainerResources();
      }
    });
  }

  GetRooms() {
    this.store.dispatch({ type: 'FETCH_ROOM_LIST' });
    this.store.select(ROOM_LIST_STATE).subscribe((responseData) => {
      this.roomList$.next(responseData);
      this.studioResource = [];
      this.roomList$.value.forEach((o: any) => {
        this.resource = {
          id: o.id,
          name: o.name_primary_language,
          img: '',
          color: '',
        };
        this.studioResource.push(this.resource);
      });
      this.updateStudioResources();
    });
  }

  updateTrainerResources(): void {
    this.trainerResources = this.getAccessedStaffList$.value.map((o: any) => ({
      ResourceId: o.id,
      name: o.name,
      img: o.ff_picture,
      color: '#1976d2',
    }));
  }

  updateStudioResources(): void {
    this.studioResource = this.roomList$.value.map((o: any) => ({
      ResourceId: o.id,
      name: o.name_primary_language || o.name_secondary_language,
      color: '#4caf50',
    }));
  }

  updateGroupSettings(): void {
    if (this.isTrainerView) {
      this.group = {
        resources: ['Trainers'],
        allowGroupEdit: false,
        byDate: false,
        enableCompactView: false,
      };
    } else if (this.isStudioView) {
      this.group = {
        resources: ['Studios'],
        allowGroupEdit: false,
        byDate: false,
        enableCompactView: false,
      };
    } else {
      this.group = {
        resources: [],
        allowGroupEdit: false,
        byDate: false,
      };
    }

    // Manually update group settings if schedule is already created
    const scheduleObj = this.scheduleObj();
    if (scheduleObj) {
      scheduleObj.group = this.group;
    }
  }

  GetClassAppointments(startDate: string, endDate: string) {
    if (!startDate || !endDate || !this.tenantId) return;

    this.loading.next(true);

    const getAppointmentRequest: ListAppointmentRequest = {
      tenant_id: this.tenantId,
      start_datetime: startDate,
      end_datetime: endDate,
    };

    const getClassesRequest: ListClassRequest = {
      tenant_id: this.tenantId,
      start_date: startDate,
      end_date: endDate,
      getClientDetails: this.currentView === 'Agenda',
    };

    forkJoin({
      appointments: this.appointmentService
        .GetAppointments(getAppointmentRequest)
        .pipe(
          catchError((error) => {
            console.error('Error fetching appointments:', error);
            return of({ items: [] });
          }),
        ),
      classes: this.classService.GetClasses(getClassesRequest).pipe(
        catchError((error) => {
          console.error('Error fetching classes:', error);
          return of({ items: [] });
        }),
      ),
    })
      .pipe(
        map((response) => {
          const appointments: Appointment[] = response.appointments.items ?? [];
          const classes: Class[] = response.classes.items ?? [];

          // Process dates to local time
          this.appointmentList$ = appointments.map((appt) => ({
            ...appt,
            start_datetime: this.timeService.ConvertUTCToLocalDateTime(
              appt.start_datetime,
            ),
            end_datetime: this.timeService.ConvertUTCToLocalDateTime(
              appt.end_datetime,
            ),
          }));

          this.classList$ = classes.map((cls) => ({
            ...cls,
            start_datetime: this.timeService.ConvertUTCToLocalDateTime(
              cls.start_datetime,
            ),
            end_datetime: this.timeService.ConvertUTCToLocalDateTime(
              cls.end_datetime,
            ),
          }));

          // Filter based on component inputs
          let filteredAppointments = this.appointmentList$;
          let filteredClasses = this.classList$;

          if (this.showOnlyAppointments()) {
            filteredClasses = [];
          }

          if (this.showOnlyClasses()) {
            filteredAppointments = [];
          }

          if (this.onlyTrialAllowed()) {
            filteredAppointments = [];
            filteredClasses = filteredClasses.filter((c) => c.accepts_trial);
          }

          return this.CreateEvents(filteredAppointments, filteredClasses);
        }),
      )
      .subscribe({
        next: (data: any[]) => {
          this.eventSettings = { ...this.eventSettings, dataSource: data };
          this.loading.next(false);
        },
        error: (error: any) => {
          console.error('Error fetching schedule data:', error);
          this.appointmentList$ = [];
          this.classList$ = [];
          this.eventSettings = { ...this.eventSettings, dataSource: [] };
          this.loading.next(false);
        },
      });
  }

  // Convert appointments and classes to Syncfusion event format
  CreateEvents(appointments: Appointment[], classes: Class[]): any[] {
    const events: any[] = [];

    appointments.forEach((appt) => {
      if (!appt.start_datetime || !appt.end_datetime) return;

      // Apply status class for cancelled appointments
      const statusClass =
        appt.status === AppointmentStatus.Cancelled ? 'line-through' : '';

      const event: any = {
        Guid: appt.guid,
        Id: appt.guid,
        Subject:
          appt.type_name_primary_language || appt.type_name_secondary_language,
        StartTime: appt.start_datetime,
        EndTime: appt.end_datetime,
        IsAllDay: false,
        Type: ScheduleType.Apppintment.toString(),
        OriginalData: appt,
        CssClass: `${statusClass}`.trim(),
        CategoryColor: appt.type_color,
      };

      // Set ResourceId based on current view
      if (this.isTrainerView && appt.coach_id) {
        event.ResourceId = appt.coach_id;
      } else if (this.isStudioView && appt.room_id) {
        event.ResourceId = appt.room_id;
      } else {
        event.ResourceId = null;
      }

      events.push(event);
    });

    classes.forEach((cls) => {
      if (!cls.start_datetime || !cls.end_datetime) return;

      // Apply status class for cancelled classes
      const statusClass =
        cls.status === ClassStatus.Cancelled ? 'line-through' : '';

      // Add virtual class indicator if needed
      const virtualClass = cls.is_virtual ? 'virtual-class' : '';

      const event: any = {
        Guid: cls.guid,
        Id: cls.guid,
        Subject:
          cls.type_name_primary_language || cls.type_name_secondary_language,
        StartTime: cls.start_datetime,
        EndTime: cls.end_datetime,
        IsAllDay: false,
        Type: ScheduleType.Class.toString(),
        OriginalData: cls,
        CssClass: `${statusClass} ${virtualClass}`.trim(),
        CategoryColor: cls.type_color,
      };

      // Set ResourceId based on current view
      if (this.isTrainerView && cls.coach_id) {
        event.ResourceId = cls.coach_id;
      } else if (this.isStudioView && cls.room_id) {
        event.ResourceId = cls.room_id;
      } else {
        event.ResourceId = null;
      }

      events.push(event);
    });

    return events;
  }

  // Event popup handling
  openClassAppointmentPopup(eventData: any) {
    const originalData = eventData.OriginalData;
    const type = eventData.Type;

    if (type === ScheduleType.Apppintment.toString()) {
      this.eventObject = eventData;
      this.appointmentObject = originalData as Appointment;
      this.emitEvents.emit({
        eventData: this.eventObject,
        appointmentData: this.appointmentObject,
      });
    } else if (type === ScheduleType.Class.toString()) {
      this.eventObject = eventData;
      this.classObject = originalData as Class;
      this.emitEvents.emit({
        eventData: this.eventObject,
        classData: this.classObject,
      });
    }
  }

  // Helper methods for templates
  getAppointmentFromData(data: any): Appointment {
    return data.OriginalData as Appointment;
  }

  getClassFromData(data: any): Class {
    return data.OriginalData as Class;
  }

  getClassDetailsForAgenda(data: any): Class {
    return data.OriginalData as Class;
  }

  // Public methods that may be called from parent components
  reloadEvents(_event: unknown = null): void {
    this.loadDataForCurrentView(true);
  }

  refreshEvents(): void {
    const events = this.CreateEvents(this.appointmentList$, this.classList$);
    this.eventSettings = { ...this.eventSettings, dataSource: events };
    const scheduleObj = this.scheduleObj();
    if (scheduleObj) {
      scheduleObj.eventSettings = this.eventSettings;
    }
  }

  // Form control getters
  get getCalenderViewEnum() {
    return CalendarView;
  }
}
